export type Layer = {
  id: string;
  type: "text" | "image";
  text: string;
  assetId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  opacity: number;
  z: number;
  visible: boolean;
  color: string;
  fontSize: number;
};
export type Design = {
  width: number;
  height: number;
  backgroundColor: string;
  backgroundAsset: string;
  backgroundFit?: "cover" | "contain";
  backgroundX?: number;
  backgroundY?: number;
  backgroundScale?: number;
  backgroundBlur?: number;
  motionBlur?: number;
  layout:
    "centerstage" | "filmstrip" | "spotlight" | "grid" | "collage" | "floating";
  duration: number;
  transition:
    "cinematic" | "rise" | "fade" | "slide" | "zoom" | "crossfade" | "cut";
  motion?: "gentle" | "still";
  transitionMs: number;
  count: number;
  columns: number;
  gap: number;
  radius: number;
  shadow: boolean;
  fit: "cover" | "contain";
  photoX: number;
  photoY: number;
  photoWidth: number;
  photoHeight: number;
  photoZ: number;
  showTitle: boolean;
  layers: Layer[];
};
export type Media = {
  id: string;
  name: string;
  mime: string;
  url: string;
  originalUrl: string;
  width: number;
  height: number;
  created: string;
};
export type EventConfig = {
  name: string;
  date: string;
  venue: string;
  status: "live" | "paused" | "archived";
  mode: "local" | "drive" | "hybrid";
  folder: string;
  driveFolder: string;
  drivePublic?: boolean;
  guestDestination?: "local" | "drive";
  driveImport?: boolean;
  galleryEnabled: boolean;
  captureEnabled: boolean;
  guestBaseUrl: string;
  design: Design;
};
export type EventData = EventConfig & {
  driveWarning?: string;
  eventAccess?: { username: string; enabled: boolean } | null;
  id: string;
  displayToken: string;
  galleryToken: string;
  captureToken: string;
  created: string;
  revision: number;
  photoCount?: number;
  photos?: Media[];
  assets?: Media[];
  sourceStatus?: {
    driveConfigured: boolean;
    error: string | null;
    jobs: { status: string; count: number }[];
  };
};
export type Snapshot = {
  id: string;
  name: string;
  status: EventConfig["status"];
  design: Design;
  photos: Media[];
  assets: Media[];
  revision: number;
};
export const defaultDesign: Design = {
  width: 1920,
  height: 1080,
  backgroundColor: "#0a1740",
  backgroundAsset: "",
  layout: "centerstage",
  duration: 14,
  transition: "cinematic",
  transitionMs: 2200,
  motion: "gentle",
  count: 7,
  columns: 3,
  gap: 18,
  radius: 12,
  shadow: true,
  fit: "cover",
  photoX: 5,
  photoY: 18,
  photoWidth: 90,
  photoHeight: 72,
  photoZ: 0,
  showTitle: true,
  layers: [],
};
export const templates = [
  {
    id: "centerstage",
    name: "Royal blue",
    note: "One hero. A constellation of moments.",
    color: "#0a1740",
    design: {
      ...defaultDesign,
      backgroundColor: "#0a1740",
      fit: "contain" as const,
      photoX: 3,
      photoY: 15,
      photoWidth: 94,
      photoHeight: 80,
      radius: 18,
    },
  },
  {
    id: "filmstrip",
    name: "Editorial",
    note: "A hero image above a quiet filmstrip.",
    color: "#272326",
    design: {
      ...defaultDesign,
      layout: "filmstrip" as const,
      backgroundColor: "#272326",
      transition: "rise" as const,
      duration: 16,
      transitionMs: 2600,
      fit: "contain" as const,
      count: 6,
    },
  },
  {
    id: "botanical",
    name: "Botanical",
    note: "Warm, natural, effortless",
    color: "#142720",
    design: { ...defaultDesign, layout: "grid" as const },
  },
  {
    id: "noir",
    name: "Midnight gala",
    note: "A spotlight for every guest",
    color: "#171722",
    design: {
      ...defaultDesign,
      backgroundColor: "#171722",
      layout: "spotlight" as const,
      count: 1,
      photoX: 15,
      photoWidth: 70,
      photoY: 15,
      photoHeight: 75,
      radius: 4,
    },
  },
  {
    id: "sand",
    name: "Golden hour",
    note: "A softer kind of celebration",
    color: "#b88a5b",
    design: {
      ...defaultDesign,
      backgroundColor: "#b88a5b",
      layout: "collage" as const,
      count: 5,
      radius: 0,
      gap: 24,
    },
  },
];
export const blankEvent = (): EventConfig => ({
  name: "",
  date: "",
  venue: "",
  status: "live",
  mode: "local",
  folder: "",
  driveFolder: "",
  galleryEnabled: false,
  captureEnabled: false,
  guestBaseUrl: "",
  design: structuredClone(defaultDesign),
});
